import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Shield,
  Award,
  Search as SearchIcon,
  Filter,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Zap,
  Loader2,
} from 'lucide-react';

import { BreachRecordList, fetchLatestBreachRecords, BREACH_RECORD_NOTE } from '@/components/kypa/BreachRecords';
import { SellerAgentBadge } from '@/components/kypa/SellerAgentBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  Bronze: { text: 'text-amber-500', border: 'border-amber-700/40', bg: 'from-amber-800/20 to-amber-900/5' },
  Silver: { text: 'text-slate-300', border: 'border-slate-400/40', bg: 'from-slate-400/15 to-slate-500/5' },
  Gold: { text: 'text-yellow-400', border: 'border-yellow-500/40', bg: 'from-yellow-500/15 to-yellow-600/5' },
  Platinum: { text: 'text-cyan-300', border: 'border-cyan-400/40', bg: 'from-cyan-400/15 to-blue-500/10' },
};

const FLAG_STYLES = {
  None: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'Clean' },
  Yellow: { text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', label: 'Yellow' },
  Orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', label: 'Orange' },
  Red: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Red' },
};

function fmtUsd(n) {
  if (n == null) return '-';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toLocaleString()}`;
}

function AgentCard({ agent, breach }) {
  const tier = TIER_STYLES[agent.pledge_tier] || TIER_STYLES.Bronze;
  const flag = FLAG_STYLES[agent.warning_flag] || FLAG_STYLES.None;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`rounded-2xl border ${tier.border} bg-gradient-to-br ${tier.bg} p-5 flex flex-col hover:border-white/40 transition-colors`}
      data-testid={`kypa-agent-card-${agent.kypa_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Award className={`w-5 h-5 ${tier.text}`} />
          <span className={`text-[10px] uppercase tracking-widest font-bold ${tier.text}`}>
            {agent.pledge_tier}
          </span>
        </div>
        <div
          className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${flag.bg} ${flag.text} border ${flag.border}`}
        >
          {flag.label}
        </div>
      </div>
      <h3 className="text-lg font-semibold text-white leading-snug mb-1">{agent.agent_name}</h3>
      <p className="text-xs text-gray-500 font-mono mb-3 truncate">{agent.kypa_id}</p>
      <SellerAgentBadge agent={agent} testid={`kypa-seller-badge-${agent.kypa_id}`} />
      {agent.description && (
        <p className="text-sm text-gray-400 leading-relaxed mb-3 line-clamp-2">{agent.description}</p>
      )}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-lg bg-black/30 border border-white/5 p-2">
          <div className="text-[9px] uppercase tracking-widest text-gray-500">Pledge on record</div>
          <div className="text-sm font-mono text-emerald-300">{fmtUsd(agent.pledge_amount_usdc)}</div>
        </div>
        <div className="rounded-lg bg-black/30 border border-white/5 p-2">
          <div className="text-[9px] uppercase tracking-widest text-gray-500">(ACS)™</div>
          <div className="text-sm font-mono text-white">{agent.acs_score}</div>
        </div>
        <div className="rounded-lg bg-black/30 border border-white/5 p-2">
          <div className="text-[9px] uppercase tracking-widest text-gray-500">Breach records</div>
          <div className={`text-sm font-mono ${breach?.total ? 'text-red-400' : 'text-white'}`} data-testid={`kypa-agent-breach-count-${agent.kypa_id}`}>
            {breach?.total ?? 0}
          </div>
        </div>
      </div>
      <div className="mb-3" data-testid={`kypa-agent-breach-feed-${agent.kypa_id}`}>
        <div className="text-[9px] uppercase tracking-widest text-gray-500 mb-1.5">Latest breach records</div>
        <BreachRecordList records={breach?.records} compact testid={`kypa-agent-breach-list-${agent.kypa_id}`} />
      </div>
      {/* ACS tier widening: shows the graduated autonomy envelope */}
      {(() => {
        const TIER_MULT = { Bronze: 1.0, Silver: 2.0, Gold: 3.5, Platinum: 5.0 };
        const mult = TIER_MULT[agent.pledge_tier] ?? 1.0;
        const baseline = 200;
        const effective = baseline * mult;
        return (
          <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] p-2 mb-3" data-testid={`acs-widening-${agent.kypa_id}`}>
            <div className="flex items-center justify-between">
              <div className="text-[9px] uppercase tracking-widest text-cyan-300">Effective cap ({agent.pledge_tier} · {mult}×)</div>
              <div className="text-xs font-mono font-bold text-cyan-200">${baseline}→${effective.toFixed(0)}</div>
            </div>
            <div className="text-[9px] text-slate-500 mt-0.5">Trust history widens per-tx cap from the mandate baseline</div>
          </div>
        );
      })()}
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500">
          <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-gray-400">
            {agent.framework}
          </span>
          <span className="text-emerald-400">{agent.verification_score}</span>
        </div>
        <Link
          to={`/kypa/agent/${agent.kypa_id}`}
          className="text-xs text-red-300 hover:text-red-200 inline-flex items-center gap-1"
          data-testid={`kypa-agent-link-${agent.kypa_id}`}
        >
          Profile <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </motion.div>
  );
}

export default function KYPADirectory() {
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('');
  const [framework, setFramework] = useState('');
  const [role, setRole] = useState('');
  const [minAcs, setMinAcs] = useState('');
  const [sort, setSort] = useState('pledge_amount_usdc');
  const [breaches, setBreaches] = useState({});
  const breachTotal = Object.values(breaches).reduce((s, b) => s + b.total, 0);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/kypa/stats`);
      const d = await r.json();
      setStats(d);
    } catch {
      /* stats are non-critical */
    }
  }, []);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '30', sort, order: 'desc' });
      if (q) params.set('q', q);
      if (tier) params.set('tier', tier);
      if (framework) params.set('framework', framework);
      if (role) params.set('role', role);
      if (minAcs) params.set('min_acs', minAcs);
      const r = await fetch(`${API}/api/kypa/search?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setAgents(d.results || []);
      setTotal(d.total || 0);
      fetchLatestBreachRecords((d.results || []).map((a) => a.kypa_id)).then(setBreaches).catch(() => setBreaches({}));
    } catch (err) {
      setError(err.message);
      setAgents([]);
    }
    setLoading(false);
  }, [q, tier, framework, role, minAcs, sort]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const t = setTimeout(fetchAgents, 250);
    return () => clearTimeout(t);
  }, [fetchAgents]);

  const clearFilters = () => {
    setQ('');
    setTier('');
    setFramework('');
    setMinAcs('');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="kypa-directory-page">
      <Helmet>
        <title>KYPA Directory: Search Pledged Agents | Kojiru</title>
        <meta
          name="description"
          content="Public search of every verified, pledged agent in the Kojiru KYPA Directory. Filter by tier, framework, and ACS score, and check each agent's public breach records."
        />
      </Helmet>
      <Navbar />

      {/* Deprecation banner: old KOJI-bound Hook (sits below the fixed nav) */}
      <div className="mt-14 sm:mt-16 border-y border-amber-500/40 bg-amber-500/[0.15] border-l-4 border-l-amber-500" data-testid="deprecation-banner">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed text-amber-100">
            <strong className="text-amber-200">Contract deprecated.</strong> The original KOJI-bound Hook (<code className="text-amber-300 font-mono">0x16C7…4476</code>) has been retired. The current mainnet Hook is the USDC-bound <code className="text-amber-300 font-mono">0x05F2…0874</code> this directory reflects the new deployment. <a href="/proofs?v=15" className="text-amber-200 underline hover:text-white">See /proofs for live packets.</a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-10 pb-8 px-6 border-b border-white/5" data-testid="kypa-directory-hero">
        <div className="container mx-auto max-w-6xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs uppercase tracking-widest text-red-300 mb-3">
            <Shield className="w-3.5 h-3.5" />
            KYPA Directory
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
            Search every <span className="text-emerald-400">pledged</span> agent.
          </h1>
          <p className="text-base text-gray-400 max-w-2xl">
            Filter by pledge tier, framework, and (ACS)™ score. Each card shows the agent's latest public breach records. A pledge is a public commitment signal, not insurance.
          </p>
        </div>
      </section>

      {/* Stats row */}
      <section className="py-6 px-6 border-b border-white/5 bg-white/[0.02]" data-testid="kypa-directory-stats">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Active Agents</div>
              <div className="text-2xl font-bold text-white">{stats?.total_agents ?? '-'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Pledge on Record</div>
              <div className="text-2xl font-bold text-emerald-400">{fmtUsd(stats?.total_pledged_usdc)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Platinum Tier</div>
              <div className="text-2xl font-bold text-cyan-300">{stats?.by_tier?.Platinum ?? '-'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Breach Records (shown agents)</div>
              <div className="text-2xl font-bold text-red-400" data-testid="kypa-directory-breach-total">{breachTotal}</div>
            </div>
            <div className="text-[11px] text-gray-500 leading-relaxed self-center">{BREACH_RECORD_NOTE}</div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="py-6 px-6 border-b border-white/5" data-testid="kypa-directory-filters">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <SearchIcon className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search agent name, description..."
                data-testid="kypa-filter-search"
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-red-500/50 focus:outline-none"
              />
            </div>
            {/* Tier */}
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              data-testid="kypa-filter-tier"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-red-500/50 focus:outline-none"
            >
              <option value="">All Tiers</option>
              <option value="Bronze">Bronze</option>
              <option value="Silver">Silver</option>
              <option value="Gold">Gold</option>
              <option value="Platinum">Platinum</option>
            </select>
            {/* Framework */}
            <select
              value={framework}
              onChange={(e) => setFramework(e.target.value)}
              data-testid="kypa-filter-framework"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-red-500/50 focus:outline-none"
            >
              <option value="">All Frameworks</option>
              <option value="langchain">LangChain</option>
              <option value="crewai">CrewAI</option>
              <option value="autogpt">AutoGPT</option>
              <option value="langgraph">LangGraph</option>
              <option value="protocol-native">Protocol-native</option>
              <option value="custom">Custom</option>
            </select>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="kypa-filter-role"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-red-500/50 focus:outline-none"
            >
              <option value="">All Roles</option>
              <option value="buyer">Shopper agents</option>
              <option value="seller">Seller agents</option>
            </select>
            {/* Min ACS */}
            <input
              type="number"
              value={minAcs}
              onChange={(e) => setMinAcs(e.target.value)}
              placeholder="Min ACS (300-850)"
              min="300"
              max="850"
              data-testid="kypa-filter-min-acs"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-red-500/50 focus:outline-none"
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-3">
              <Filter className="w-3.5 h-3.5" />
              <span>
                <strong className="text-white">{total}</strong> agent{total === 1 ? '' : 's'} match
              </span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="kypa-filter-sort"
                className="bg-transparent border border-white/10 rounded px-2 py-1 text-xs text-gray-400 focus:outline-none"
              >
                <option value="pledge_amount_usdc">Sort: Pledge (high→low)</option>
                <option value="acs_score">Sort: ACS (high→low)</option>
                <option value="transaction_volume_30d">Sort: 30d Volume</option>
                <option value="created_at">Sort: Newest</option>
              </select>
            </div>
            <button
              type="button"
              onClick={clearFilters}
              className="text-red-400 hover:text-red-300"
              data-testid="kypa-filter-clear"
            >
              Clear filters
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-10 px-6" data-testid="kypa-directory-results">
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="kypa-directory-loading">
              <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
            </div>
          ) : error ? (
            <div
              className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 flex items-center gap-3"
              data-testid="kypa-directory-error"
            >
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <div>
                <div className="text-white font-semibold">Failed to load directory</div>
                <div className="text-sm text-gray-400">{error}</div>
              </div>
            </div>
          ) : agents.length === 0 ? (
            <div
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center"
              data-testid="kypa-directory-empty"
            >
              <Zap className="w-10 h-10 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No agents match those filters</h3>
              <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
                The KYPA Directory is in launch mode. Be one of the first Founding Pledged Agents: enroll in under 5 minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register-agent?mode=kypa" data-testid="kypa-directory-empty-cta">
                  <Button className="bg-red-500 hover:bg-red-600 text-white font-semibold gap-2">
                    Enroll Your Agent
                    <CheckCircle2 className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/kypa">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    Read the Spec
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {agents.map((a) => (
                <AgentCard key={a.kypa_id} agent={a} breach={breaches[a.kypa_id]} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
